// Finds who a contract is between: the preamble ("by and between X ("Client") and Y"), a numbered
// "PARTIES" list, labelled lines ("Landlord: X", "Policyholder Name: X"), a letter's addressee and
// letterhead, then signature blocks ("X / By: …"). Rule-based; the user confirms the result.

export interface Party {
  name: string;
  /** The defined term the contract gives the party ("Client", "Lessor"), if any. */
  role: string | null;
}

export interface FoundParties {
  parties: Party[];
  evidence: string;
}

const ABBREVIATIONS = /\b(?:inc|ltd|co|corp|pvt|pte|llc|l\.l\.c|llp|plc|no|nos|st|mr|mrs|ms|dr|sr|jr|sh|shri|sri|smt|km|s\.a|n\.v|b\.v|p\.c|l\.p|bros|ave|rd|u\.s|e\.g|i\.e|viz|m\/s|messrs|s\/o|d\/o|w\/o|c\/o|h)$/i;

const ROLE_WORDS = new Set([
  "landlord", "tenant", "lessor", "lessee", "licensor", "licensee", "client", "customer", "vendor", "supplier",
  "provider", "service provider", "contractor", "consultant", "company", "employer", "employee", "buyer", "seller",
  "purchaser", "agency", "agent", "owner", "party", "parties", "first party", "second party", "disclosing party",
  "receiving party", "recipient", "discloser", "distributor", "reseller", "partner", "freelancer", "developer",
  "subscriber", "member", "borrower", "lender", "guarantor", "franchisor", "franchisee", "principal", "insurer",
  "insured", "policyholder", "proposer", "candidate", "applicant", "operator",
]);

/** Roles that are usually the other side for a small business tracking its own contracts. */
const OTHER_SIDE_ROLES = /^(?:landlord|lessor|licensor|owner|vendor|supplier|(?:service\s+)?provider|contractor|consultant|seller|agency|agent|developer|distributor|reseller|lender|franchisor|insurer|operator|club)$/i;
const OWN_SIDE_ROLES = /^(?:tenant|lessee|licensee|client|customer|buyer|purchaser|company|employer|subscriber|borrower|franchisee|member|policyholder|insured|proposer)$/i;

const LEGAL_SUFFIXES = new Set([
  "inc", "incorporated", "llc", "ltd", "limited", "corp", "corporation", "co", "company", "pvt", "private",
  "pte", "plc", "llp", "lp", "gmbh", "ag", "sa", "bv", "nv", "the", "and", "of", "s", "workspace", "team",
  "org", "organisation", "organization", "personal", "my", "group", "holdings",
]);

/** "Inc.", "LLC", "Pvt. Ltd." after a comma belong to the name: "Northwind Analytics, Inc.". */
const SUFFIX_AFTER_COMMA = /^(?:inc|incorporated|llc|l\.l\.c|ltd|limited|corp|corporation|co|plc|llp|l\.?p|gmbh|ag|s\.?a|n\.?v|b\.?v|pvt\.?\s*ltd|pte\.?\s*ltd)\b\.?/i;
const HONORIFIC = /^(?:mr|mrs|ms|miss|mx|shri|sri|smt|sh|kumari|kum|mister|madam)\.?\s+/i;
const COMPANY_WORDS = /\b(?:limited|ltd|llp|llc|inc|corp|corporation|company|co|pvt|private|plc|gmbh|insurance|bank|enterprises|industries|services|solutions|technologies|ventures|associates|club|studios?|foods|logistics|systems)\b\.?/i;

function clean(s: string): string {
  return s.replace(/\s+/g, " ").replace(/\s+([,.;:)])/g, "$1").replace(/([(])\s+/g, "$1").trim();
}

function titleCase(s: string): string {
  if (s !== s.toUpperCase()) return s;
  // Keep short legal forms as they're usually written.
  return s.toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase()).replace(/\b(Llp|Llc|Plc|Pvt|Gmbh|Ag|Of|And|The)\b/g, (w) => (["Of", "And", "The"].includes(w) ? w.toLowerCase() : w === "Gmbh" ? "GmbH" : w.toUpperCase()));
}

function snippet(text: string, start: number, end: number): string {
  const s = Math.max(0, start);
  const e = Math.min(text.length, end);
  return (s > 0 ? "…" : "") + text.slice(s, e).trim() + (e < text.length ? "…" : "");
}

/** The preamble after "between", up to the end of its sentence (ignoring "Inc." and text inside brackets). */
function preambleSpan(text: string, from: number): string {
  const seg = text.slice(from, from + 1500);
  let depth = 0;
  for (let i = 0; i < seg.length; i++) {
    const c = seg[i]!;
    if (c === "(") depth++;
    else if (c === ")") depth = Math.max(0, depth - 1);
    else if (depth === 0 && c === "." && /^\s+["“A-Z0-9]/.test(seg.slice(i + 1, i + 3))) {
      const word = /[\w./]+$/.exec(seg.slice(Math.max(0, i - 12), i))?.[0] ?? "";
      if (!ABBREVIATIONS.test(word) && !/\b[A-Z]$/.test(word)) return seg.slice(0, i);
    } else if (depth === 0 && /^(?:WHEREAS|RECITALS|NOW,? THEREFORE|NOW IT IS|WITNESSETH|BACKGROUND|AGREED TERMS|IT IS AGREED)\b/.test(seg.slice(i, i + 20))) {
      return seg.slice(0, i);
    }
  }
  return seg;
}

/** Splits the preamble into one chunk per party at top-level "and"s. */
function splitParties(span: string): string[] {
  const depthAt: number[] = [];
  let depth = 0;
  for (const c of span) {
    if (c === "(") depth++;
    else if (c === ")") depth = Math.max(0, depth - 1);
    depthAt.push(depth);
  }
  const strong: { start: number; end: number }[] = [];
  const weak: { start: number; end: number }[] = [];
  for (const m of span.matchAll(/\s*[,;]?\s*\b(?:and|AND|&)\s+(?=(?:the\s+)?\(?\d?\)?\s*["“]?[\p{Lu}(])/gu)) {
    if (depthAt[m.index!] !== 0) continue;
    const before = span.slice(0, m.index!).trimEnd();
    const b = { start: m.index!, end: m.index! + m[0].length };
    if (/\)$|\bpart$|\bpart;?$/i.test(before) || /^\s*[,;]/.test(m[0])) strong.push(b);
    else weak.push(b);
  }
  // Prefer "…("Client"), and …": "and" inside a name ("Johnson and Johnson") is never right after a bracket.
  const cuts = strong.length ? strong : weak.slice(0, 1);
  const chunks: string[] = [];
  let at = 0;
  for (const c of cuts) {
    chunks.push(span.slice(at, c.start));
    at = c.end;
  }
  chunks.push(span.slice(at));
  return chunks.slice(0, 6);
}

function roleOf(chunk: string): string | null {
  const quoted = /\(([^)]*)\)/.exec(chunk)?.[1];
  const inQuotes = quoted && /["“”'‘’]([^"“”'‘’]{2,40})["“”'‘’]/.exec(quoted)?.[1];
  const called = /hereinafter\s+(?:(?:jointly|collectively|individually)\s+)?(?:referred\s+to\s+as|called|known\s+as|the)\s+(?:the\s+)?["“'‘]?((?:the\s+)?[A-Za-z][\w-]*(?:\s+[A-Z][\w-]*)?)/i.exec(chunk)?.[1];
  const role = (inQuotes ?? called ?? "").replace(/^the\s+/i, "").trim();
  return role ? titleCase(role) : null;
}

/** The first comma that ends the name (not one before "Inc." or "Ltd."). */
function cutAtComma(s: string): string {
  let at = 0;
  for (;;) {
    const i = s.indexOf(",", at);
    if (i < 0) return s;
    if (!SUFFIX_AFTER_COMMA.test(s.slice(i + 1).trimStart())) return s.slice(0, i);
    at = i + 1;
  }
}

export function nameOf(chunk: string): string | null {
  let s = clean(chunk)
    .replace(/^[,;:\s]+/, "")
    .replace(/^(?:\(\d+\)|\d+[.)]|\([a-z]\)|[a-z]\))\s+/i, "")
    .replace(/^(?:M\/s\.?|Messrs\.?)\s*/i, "")
    .replace(/^the\s+(?=\p{Lu})/u, "")
    .replace(HONORIFIC, "");
  s = s.split(/\s*\(/)[0]!;
  s = cutAtComma(s);
  s = s.split(/\s+(?:having|residing|resident|located|situated|whose|with\s+(?:its|a|an)|a\s+(?:company|corporation|firm|partnership|limited|private|public|sole|body|society|trust|non[\s-]banking|delaware|texas|massachusetts|washington|california|new\s+york)|an\s+(?:individual|entity|indian|english|american)|incorporated|organi[sz]ed|registered|duly|acting|through|represented|aged?|s\/o|d\/o|w\/o|son\s+of|daughter\s+of|wife\s+of|proprietor\s+of|of\s+(?:the\s+)?(?:one|first|other|second)\s+part|of\s+\d)\b/i)[0]!;
  s = s.replace(/[,;:\s]+$/, "").trim();
  if (s.length < 2 || s.length > 120 || !/\p{L}/u.test(s)) return null;
  if (!/^[\p{Lu}\d"“]/u.test(s)) return null;
  if (ROLE_WORDS.has(s.replace(/^the\s+/i, "").toLowerCase())) return null;
  if (s.split(" ").length > 12) return null;
  return titleCase(s);
}

function dedupe(parties: Party[]): Party[] {
  const out: Party[] = [];
  for (const p of parties) {
    const key = normalizeName(p.name);
    if (!key) continue;
    const same = out.find((o) => normalizeName(o.name) === key);
    if (same) {
      same.role ??= p.role;
      continue;
    }
    out.push({ ...p });
  }
  return out;
}

function fromChunks(chunks: string[]): Party[] {
  return dedupe(
    chunks
      .map((chunk) => {
        const name = nameOf(chunk);
        const role = roleOf(chunk);
        // ("Horizon") is a short name, not a role.
        return { name, role: role && name && normalizeName(name).includes(normalizeName(role)) ? null : role };
      })
      .filter((p): p is Party => p.name !== null),
  );
}

function fromPreamble(text: string): FoundParties | null {
  const head = text.slice(0, 6000);
  const m = /\b(?:by\s+and\s+)?(?:between|among(?:st)?)\b\s*:?\s*/i.exec(head);
  if (!m) return null;
  const span = preambleSpan(text, m.index + m[0].length);
  const parties = fromChunks(splitParties(span));
  if (parties.length < 2) return null;
  return { parties, evidence: snippet(text, m.index - 30, m.index + m[0].length + Math.min(span.length, 260)) };
}

/** "PARTIES (1) Fenwick Paper Mills Limited … (the "Supplier"); and (2) Greenleaf Packaging Ltd …". */
function fromPartiesList(text: string): FoundParties | null {
  const m = /\bPARTIES\b\s*:?\s*(?=\(1\)|1[.)])/.exec(text.slice(0, 6000));
  if (!m) return null;
  const span = preambleSpan(text, m.index + m[0].length);
  const items = span.split(/(?:^|[;,]?\s*(?:and\s+)?)\(\d\)\s+|(?:^|[;,]?\s*(?:and\s+)?)\b\d[.)]\s+(?=\p{Lu})/u).filter((x) => x.trim());
  const parties = fromChunks(items);
  return parties.length >= 2 ? { parties, evidence: snippet(text, m.index, m.index + Math.min(span.length, 260)) } : null;
}

const LABEL_ROLES = [...ROLE_WORDS].filter((r) => !["party", "parties", "principal", "recipient", "discloser"].includes(r));
const LABEL_RE = new RegExp(
  // "Proposer / Policyholder : X" names one party with two labels.
  `^\\s*(?:name\\s+of\\s+(?:the\\s+)?)?(?:(?:${LABEL_ROLES.join("|")})\\s*\\/\\s*)?(${LABEL_ROLES.join("|")}|party\\s+[ab12]|(?:first|second)\\s+party)(?:['’]s)?\\s*(?:name|details)?\\s*[:\\-–]\\s*(.{2,300})$`,
  "gim",
);

function fromLabels(raw: string): Party[] {
  const parties: Party[] = [];
  for (const m of raw.slice(0, 8000).matchAll(LABEL_RE)) {
    // "First Party : RAMESH KUMAR SHAH" on an e-stamp certificate names the parties too, but without roles.
    const name = nameOf(m[2]!.split(/ {3,}/)[0]!);
    if (!name) continue;
    const role = titleCase(m[1]!.replace(/\s+/g, " ")).replace(/^\p{Ll}/u, (c) => c.toUpperCase());
    parties.push({ name, role: /^(?:first|second)\s+party$/i.test(role) ? null : role });
  }
  return dedupe(parties);
}

/** The company at the top of a letter or schedule ("SURAKSHA GENERAL INSURANCE COMPANY LIMITED", "IRONHOUSE FITNESS CLUB - MEMBERSHIP AGREEMENT"). */
function letterhead(lines: string[]): Party | null {
  for (const line of lines.slice(0, 4)) {
    const head = line.split(/\s+[-–—|]\s+/)[0]!.replace(/\(.*?\)/g, "").trim();
    if (head.length < 4 || head.length > 90 || /[:@]|\d{3}/.test(head)) continue;
    if (!COMPANY_WORDS.test(head) || /\b(?:agreement|contract|policy\s+schedule|certificate|order|deed|letter)$/i.test(head)) continue;
    const name = nameOf(head);
    if (name) return { name, role: companyRole(head) };
  }
  return null;
}

/** What a company heading a letter or schedule is, from its name: insurers and banks. */
function companyRole(name: string): string | null {
  if (/\binsurance\b|\bassurance\b/i.test(name)) return "Insurer";
  if (/\bbank\b|\bfinance\b|\bfinvest\b|\bfinancial\s+services\b|\bcredit\b|\bcapital\b|\bnbfc\b|\bhousing\s+finance\b/i.test(name)) return "Lender";
  return null;
}

/**
 * A letter: "To, Mr. Arjun Nair" (or "Dear Arjun,") and the company signing it ("For Zenith Software
 * Solutions Pvt. Ltd."). The addressee comes first: whoever the letter is to is reading it, so the
 * company is the other side.
 */
function fromLetter(lines: string[]): Party[] {
  const parties: Party[] = [];
  const to = lines.findIndex((l) => /^to,?$/i.test(l));
  const addressee = to >= 0 ? lines[to + 1] : /^dear\s+(.+?),?$/i.exec(lines.find((l) => /^dear\s+/i.test(l)) ?? "")?.[1];
  const person = addressee && /^(?:(?:mr|mrs|ms|dr|shri|smt)\.?\s+)?[\p{Lu}][\p{L}.]*(?:\s+[\p{Lu}][\p{L}.]*){0,3}$/u.test(addressee.trim()) ? nameOf(addressee) : null;
  const signer = lines.map((l) => /^for\s+(?:and\s+on\s+behalf\s+of\s+)?(.{3,100})$/i.exec(l)?.[1]).find((n) => n && COMPANY_WORDS.test(n));
  const company = signer ? nameOf(signer) : (letterhead(lines)?.name ?? null);
  if (person) parties.push({ name: person, role: null });
  if (company) parties.push({ name: company, role: companyRole(company) });
  return dedupe(parties);
}

/** "Acme Inc.\nBy: Jane Doe, CEO" — the line before each "By:" names a signing party; so does "for and on behalf of X". */
function fromSignatures(raw: string): FoundParties | null {
  const lines = raw.split(/\r?\n/).map((l) => l.trim());
  const parties: Party[] = [];
  for (let i = 0; i < lines.length; i++) {
    const forMatch = /^for\s+(?:and\s+on\s+behalf\s+of\s+)?(.{2,100})$/i.exec(lines[i]!) ?? /\bfor\s+and\s+on\s+behalf\s+of\s+(.{2,100})$/i.exec(lines[i]!);
    let candidate: string | null = forMatch ? forMatch[1]! : null;
    if (!candidate && /^By\s*:/i.test(lines[i]!)) {
      for (let j = i - 1; j >= Math.max(0, i - 3); j--) {
        const l = lines[j]!;
        if (!l || /^(?:signature|signed|name|title|date|witness)\b|^_+$/i.test(l)) continue;
        candidate = l;
        break;
      }
    }
    for (const part of candidate ? candidate.split(/ {3,}/) : []) {
      const name = nameOf(part.replace(/[:]$/, ""));
      if (name && !/^(?:in\s+witness|by\s*:)/i.test(name)) parties.push({ name, role: null });
    }
  }
  const unique = dedupe(parties);
  return unique.length >= 2 ? { parties: unique, evidence: `Signed by ${unique.map((p) => p.name).join(" and ")}` } : null;
}

/** `text` has its whitespace collapsed; `raw` keeps line breaks for labels, letters and signature blocks. */
export function findParties(text: string, raw: string = text): FoundParties | null {
  const found = fromPreamble(text) ?? fromPartiesList(text);
  if (found) return found;
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const labelled = fromLabels(raw);
  if (labelled.length >= 2) return { parties: labelled, evidence: labelled.map((p) => `${p.role ?? "Party"}: ${p.name}`).join(" · ") };
  // One labelled party (a policyholder, a member) and the company whose schedule it is.
  const head = letterhead(lines);
  if (labelled.length === 1 && head && normalizeName(head.name) !== normalizeName(labelled[0]!.name)) {
    const parties = [head, labelled[0]!];
    return { parties, evidence: parties.map((p) => `${p.role ?? "Party"}: ${p.name}`).join(" · ") };
  }
  const letter = fromLetter(lines);
  if (letter.length >= 2) return { parties: letter, evidence: `Letter from ${letter[1]!.name} to ${letter[0]!.name}` };
  return fromSignatures(raw);
}

/** "Acme Corp., Inc." → "acme"; used to match a party against the user's own organisation. */
export function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/['’]s\b/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .split(" ")
    .filter((w) => w && !LEGAL_SUFFIXES.has(w))
    .join(" ");
}

function isOwn(party: Party, ownNames: readonly string[]): boolean {
  const p = normalizeName(party.name);
  if (!p) return false;
  const pTokens = new Set(p.split(" ").filter((t) => t.length >= 3));
  return ownNames.some((own) => {
    const o = normalizeName(own);
    if (!o) return false;
    if (o === p || (o.length >= 4 && p.includes(o)) || (p.length >= 4 && o.includes(p))) return true;
    return o.split(" ").some((t) => t.length >= 3 && pTokens.has(t));
  });
}

export type CounterpartyReason = "own-name" | "role" | "order";

/** Which party is the other side: the one that isn't the user's organisation, else by role, else the second. */
export function pickCounterparty(parties: readonly Party[], ownNames: readonly string[] = []): { party: Party; reason: CounterpartyReason } | null {
  if (parties.length === 0) return null;
  if (parties.length === 1) return { party: parties[0]!, reason: "order" };
  const own = parties.filter((p) => isOwn(p, ownNames));
  if (own.length > 0 && own.length < parties.length) {
    return { party: parties.find((p) => !own.includes(p))!, reason: "own-name" };
  }
  const other = parties.filter((p) => p.role && OTHER_SIDE_ROLES.test(p.role));
  if (other.length === 1) return { party: other[0]!, reason: "role" };
  const self = parties.filter((p) => p.role && OWN_SIDE_ROLES.test(p.role));
  if (self.length === 1 && parties.length === 2) return { party: parties.find((p) => p !== self[0])!, reason: "role" };
  return { party: parties[1]!, reason: "order" };
}
